// Blog articles, long-form SEO content that captures high-intent searches.
// All content is original to Yellow Video Production.

export const BLOG_POSTS = [
  {
    slug: "how-much-does-a-video-cost-liverpool",
    title: "How Much Does a Video Cost in Liverpool? (2026 Pricing Guide)",
    metaTitle: "How Much Does a Video Cost in Liverpool? | 2026 Pricing Guide",
    metaDescription:
      "Real video production prices in Liverpool, what £500, £2,500 and £10,000 buys you, and how to spot a quote that's too good to be true.",
    keywords:
      "how much does a video cost liverpool, video production cost liverpool, video production prices liverpool, cheap video production liverpool, video pricing guide",
    publishedAt: "2026-01-10",
    readTime: "8 min read",
    excerpt:
      "Three videographers will quote you three wildly different prices for the same brief. Below is a real breakdown of what each price bracket gets you in Liverpool today.",
    cover:
      "/thumbs/286504818.jpg",
    sections: [
      {
        h2: "TL;DR: Video production prices in Liverpool (2026)",
        body:
          "A serviceable corporate video in Liverpool typically falls into one of three brackets:\n\n• **£500–£1,500**, A one-person videographer with a single camera. Great for a basic event recap or a quick social post.\n• **£2,500–£6,000**, A small crew, planning, edited multi-cam coverage, motion graphics. The 'most projects live here' tier.\n• **£8,000–£25,000+**, Full production: scripting, presenters, drone, animation, multi-day shoots, broadcast-quality kit.\n\nIf a quote sits dramatically below the tier you need, ask exactly what's missing, it's usually planning, contingency, or revisions.",
      },
      {
        h2: "What you're paying for",
        body:
          "When you buy a video, you're rarely just buying 'a video'. You're buying:\n\n1. **Planning**, pre-production calls, shot lists, scripting, scheduling. Easily 20–30% of the price tag.\n2. **Crew & kit**, one operator vs. a director, camera op, sound recordist, lighting, gimbal/jib.\n3. **Edit time**, a polished 90-second brand film can take a senior editor 2–3 days.\n4. **Motion design & graphics**, title cards, lower thirds, animated stats, brand stings.\n5. **Music licensing**, a single track from a legit library is £30–£200; royalty-free isn't free if you want to look credible.\n6. **Revisions & rights**, 2 rounds of changes vs. unlimited, plus copyright assignment.",
      },
      {
        h2: "Why two Liverpool videographers can quote 4× different prices",
        body:
          "Same brief, two different worlds. The cheap quote almost always cuts one of: planning, lighting, sound, edit time, music licensing, revisions, or photographer add-ons. Yellow's package includes all of the above plus **free stills photography worth £800**, so the headline price isn't always comparing like for like.",
      },
      {
        h2: "How to brief a Liverpool video production company properly",
        body:
          "A great brief gets you a great quote. The three questions that change the price most:\n\n1. **Where is it filmed?** (Liverpool city centre, multiple sites, all over the UK?)\n2. **How many videos do you need?** (One hero film, or one hero + 10 social cut-downs?)\n3. **When do you need it?** (Standard 2-week turnaround vs. same-week rush?)\n\nIf you can answer those three plus 'what's the video for' and 'who's the audience', you'll get a tight, accurate quote.",
      },
      {
        h2: "Yellow's typical brackets",
        body:
          "We deliberately don't list fixed prices on a rate card, every project is different. But broadly: **Micro Video** (short-form social drops) starts around £1,200. **Standard Production** (the most-booked tier, a hero brand film + cut-downs + free photography) usually lands £3,500–£7,500. **Campaign / Series** for multi-day or multi-film projects sits £10,000+. Fixed-price quote within 4 working hours when you fill in the brief form.",
      },
    ],
    cta: {
      text: "Get a fixed-price quote for your Liverpool video in 4 hours.",
      href: "/contact",
    },
  },
  {
    slug: "video-production-vs-videographer-liverpool",
    title: "Video Production Company vs Videographer in Liverpool: Which One Do You Need?",
    metaTitle: "Video Production Company vs Videographer Liverpool | Which to Hire",
    metaDescription:
      "The honest difference between a videographer and a video production company in Liverpool, and when each one is the right call.",
    keywords:
      "video production company vs videographer, videographer liverpool, video production liverpool, hire videographer liverpool, video production company liverpool",
    publishedAt: "2026-01-08",
    readTime: "6 min read",
    excerpt:
      "Both will turn up with a camera and leave you with a video. The difference is what happens before, between and after, and which one is right depends on the job, not the price tag.",
    cover:
      "/thumbs/524247054.jpg",
    sections: [
      {
        h2: "The honest definition",
        body:
          "**A videographer** is one talented person with a camera. They shoot, often edit, and usually work alone.\n\n**A video production company** is a crew. Producer, director, camera op, sound, lighting, editor, motion designer, sometimes a presenter and a drone pilot. They plan, film, edit AND project-manage.",
      },
      {
        h2: "When a videographer is exactly right",
        body:
          "• Same-day event coverage where one person needs to move quickly\n• A simple talking-head clip\n• Tight budgets where you'll do your own scripting and edit direction\n• Personal projects (weddings, family films)\n\nIf you know exactly what you want, can direct yourself and don't need motion graphics, a videographer in Liverpool can absolutely deliver.",
      },
      {
        h2: "When you need a video production company",
        body:
          "• Multi-day or multi-location shoots\n• Anything that needs scripting, motion graphics or animation\n• Brand films, recruitment campaigns, training series\n• Multiple deliverables (1 hero + 10 cut-downs)\n• Tight deadlines that require parallel work (shoot Tuesday, deliver Friday)\n• Anything where a single point of failure is too risky",
      },
      {
        h2: "The hidden cost question",
        body:
          "A solo videographer's day rate looks cheaper. But a production company often delivers more usable assets per day because the crew works in parallel: while the DOP is lighting the next setup, the producer is briefing the next interviewee. Cost-per-deliverable often flips in the production company's favour.",
      },
      {
        h2: "What Yellow is",
        body:
          "We're a Liverpool-based video production company, but our pricing scales, for a single short, you'll get a 2–3 person crew. For a campaign, you'll get a director, full crew, drone pilot and animation team. Same project management, same QA, same delivery standard at every tier.",
      },
    ],
    cta: {
      text: "Not sure which you need? Send us your brief.",
      href: "/contact",
    },
  },
  {
    slug: "best-corporate-video-examples-liverpool-2026",
    title: "9 Best Corporate Video Examples from Liverpool Brands (2026)",
    metaTitle: "9 Best Corporate Video Examples from Liverpool Brands | 2026",
    metaDescription:
      "A curated reel of the strongest corporate video work coming out of Liverpool in 2026, and the production lessons behind each one.",
    keywords:
      "corporate video examples liverpool, best corporate video liverpool, corporate video production liverpool, liverpool brand video examples, b2b video examples uk",
    publishedAt: "2026-01-05",
    readTime: "7 min read",
    excerpt:
      "Nine brand films made in or for Liverpool that punch well above their weight, and exactly what makes each one work.",
    cover:
      "/thumbs/286504818.jpg",
    sections: [
      {
        h2: "Why corporate video looks tired (and how to fix it)",
        body:
          "Most 'corporate video' in 2026 still falls into the same traps: dull office shots, awkward CEO pieces-to-camera, jargon-stuffed VO. The good ones break those patterns by leaning into specifics, real customers, real outcomes, real moments. Here are nine that get it right.",
      },
      {
        h2: "1. MADE at Moddershall Oaks: facility film",
        body:
          "**What it does well:** Treats a wellness facility like a high-end hotel ad. No talking heads. Pure mood, light, water, breath, movement. The viewer feels relaxed before the brand even introduces itself.\n\n**Lesson:** Sometimes 'show, don't tell' means deleting the script entirely.",
      },
      {
        h2: "2. Liverpool Hope University: 'Say Yes'",
        body:
          "**What it does well:** Students tell their own stories, in their own words. No 'enrolment officer' voiceover, no overproduced graphics. Just trust.\n\n**Lesson:** For higher-ed and recruitment marketing, the best brand voice is somebody else's.",
      },
      {
        h2: "3. Amplifon: 'Dedicated to Better Hearing'",
        body:
          "**What it does well:** A customer testimonial that doesn't feel like a testimonial. The interviewee was made comfortable enough to forget the camera, so the emotion is real.\n\n**Lesson:** The producer's job isn't to point the camera, it's to make the on-screen person trust you.",
      },
      {
        h2: "4. DeTrafford: investor build-progress films",
        body:
          "**What it does well:** Drone + ground crew quarterly updates that turn 'construction site' into 'investor confidence machine'.\n\n**Lesson:** Recurring video content (monthly or quarterly) beats one-off mega-films for any audience that needs trust over time.",
      },
      {
        h2: "5. Cancer Research UK: skin cancer awareness",
        body:
          "**What it does well:** Mixes medical expert authority with survivor emotion. Earns the right to ask viewers to change behaviour.\n\n**Lesson:** Public-interest video lives or dies on emotional honesty. Don't sand it down.",
      },
      {
        h2: "6. Bob's Business: cyber security animation",
        body:
          "**What it does well:** Animated explainer that makes a technical topic friendly without dumbing it down.\n\n**Lesson:** Animation isn't for when you have no footage, it's for when no footage could explain it better.",
      },
      {
        h2: "7. Onward Homes: team introduction",
        body:
          "**What it does well:** Introduces an in-house team by showing them doing their actual jobs. No fake high-fives.\n\n**Lesson:** Hold the staged shots. Wait for the real ones.",
      },
      {
        h2: "8. Persil 'Cook with Kids': Unilever",
        body:
          "**What it does well:** A 5-part series with a children's cooking expert. Repeatable format, lots of usable cut-downs, embeds smoothly across social and web.\n\n**Lesson:** Don't make one video. Make a format.",
      },
      {
        h2: "9. T3DMC: 3D scanner demo",
        body:
          "**What it does well:** Mixes live product footage with motion graphics to show what the scanner is 'seeing', a thing the camera literally cannot show.\n\n**Lesson:** Motion graphics earn their keep when they reveal what footage alone can't.",
      },
    ],
    cta: {
      text: "Want one of these on your homepage next quarter? Let's talk.",
      href: "/contact",
    },
  },
  {
    slug: "questions-to-ask-video-production-company-liverpool",
    title: "10 Questions to Ask a Video Production Company in Liverpool Before You Hire",
    metaTitle: "10 Questions to Ask a Liverpool Video Production Company Before Hiring",
    metaDescription:
      "The 10 questions every Liverpool buyer should ask a video production company before signing the contract, including the ones most companies hope you forget.",
    keywords:
      "hire video production company liverpool, video production company liverpool questions, choose video production company liverpool, finding a videographer liverpool, video production company checklist",
    publishedAt: "2026-01-15",
    readTime: "9 min read",
    excerpt:
      "Most Liverpool video buyers ask the wrong questions and end up disappointed. Here are the ten that protect your budget and get you the right crew.",
    cover:
      "/thumbs/485528301.jpg",
    sections: [
      {
        h2: "Why the brief matters more than the price tag",
        body:
          "Most Liverpool businesses pick a video production company on price alone. Three quotes come in, the middle one wins. That works until the day you realise the cheap quote was missing something obvious (planning, lighting, sound, revisions) and you're £4,000 in with a video your CEO won't sign off.\n\nThe questions below are the ones that surface those gaps before you commit.",
      },
      {
        h2: "1. Can I see three projects similar to mine?",
        body:
          "Not 'see the showreel'. Specifically: similar industry, similar size, similar deliverable. A company that films weddings beautifully may not be the right call for a corporate recruitment film. Ask to see the closest matches.\n\nIf they can't show you three, they probably haven't done your type of work before.",
      },
      {
        h2: "2. Who is on the crew on shoot day?",
        body:
          "Will it be one person juggling camera, sound and lighting? A two-person team? A full crew of five? The answer drastically changes both the quality and the cost.\n\nFollow-up: ask if any of the crew on the shoot will be sub-contracted. Sub-contracting isn't bad in itself, but you want to know who you're getting.",
      },
      {
        h2: "3. What's included beyond filming and editing?",
        body:
          "The cheap quote nearly always omits one or more of: scripting, music licensing, motion graphics, multiple cut-downs, revisions, project management calls, stills photography, copyright assignment.\n\nGet a list of inclusions in writing before you compare quotes. Yellow's package includes free stills photography worth £800, motion graphics, music licensing and unlimited copyright as standard. Not every Liverpool video production company does.",
      },
      {
        h2: "4. How many rounds of revisions do I get?",
        body:
          "Two rounds is industry standard. Some companies offer 'unlimited' revisions, which sounds great until you realise it just means they didn't plan properly upfront and you'll be re-editing for weeks.\n\nUnlimited revisions can be a red flag. Two well-defined rounds with a clear feedback process is usually the better deal.",
      },
      {
        h2: "5. Who owns the footage and the final video?",
        body:
          "This catches people out. Some videographers keep ownership and license you to use the file. Others give you full copyright and unlimited usage. Big difference if you ever want to re-edit, re-purpose, or hand the project to another agency.\n\nAsk: 'Do I own the final video and the raw footage outright?' If the answer is no, find out exactly what you can and can't do with it.",
      },
      {
        h2: "6. What happens if filming gets cancelled or rescheduled?",
        body:
          "Weather, illness, restricted access, equipment failure. Things go wrong. Ask up front:\n\n• What's your cancellation policy?\n• Do you have backup kit and backup crew if mine doesn't show up?\n• If we have to reschedule, what's the cost?",
      },
      {
        h2: "7. What's your turnaround time?",
        body:
          "Standard turnaround in Liverpool is around 2 weeks from shoot to first cut. Anything quicker than that needs extra crew or rush fees. Anything slower than 4 weeks is a warning sign, that's usually a sole trader bottlenecked on editing.\n\nMatch the turnaround promise to your launch deadline with a 2-week buffer.",
      },
      {
        h2: "8. Can you show me a previous customer I can call?",
        body:
          "References. Most companies will offer them. The good ones will offer them proactively. If you have to drag a reference out of someone, that's data.\n\nWhen you call, ask: 'Would you book them again? What's the one thing you'd warn me about?' The honest answer to question two tells you everything.",
      },
      {
        h2: "9. What's the payment structure?",
        body:
          "A reasonable structure for Liverpool video production is 50% deposit on signature, 50% on delivery. Some companies want 100% up front for shorter projects, that's fine for small jobs.\n\nWatch for: 100% up front on bigger jobs, no clear delivery milestone, vague invoice timing. These create disputes.",
      },
      {
        h2: "10. What's the brief in 30 seconds?",
        body:
          "Reverse it on them. After you've explained your project, ask them to play back the brief in 30 seconds. If they can't summarise what you need, the planning hasn't sunk in yet, and you'll see it in the final video.\n\nThe best Liverpool video production companies don't just take a brief, they reframe it back to you with one or two questions you hadn't thought to ask.",
      },
    ],
    cta: {
      text: "Want straight answers to all 10 questions in a 15-minute call?",
      href: "/contact",
    },
  },
  {
    slug: "how-long-does-video-production-take-liverpool",
    title: "How Long Does Video Production Take? A Liverpool Production Timeline Guide",
    metaTitle: "How Long Does Video Production Take in Liverpool? | Timeline Guide",
    metaDescription:
      "A realistic week-by-week timeline for video production in Liverpool, from brief to delivery, plus the fastest turnaround we've ever hit.",
    keywords:
      "how long does video production take, video production timeline liverpool, video production turnaround liverpool, video production schedule liverpool, how long to make a video, video production lead time",
    publishedAt: "2026-01-12",
    readTime: "6 min read",
    excerpt:
      "If you've been promised a polished corporate video in 'a few days', someone's about to cut corners. Here's a realistic Liverpool production timeline, and where the time actually goes.",
    cover:
      "/thumbs/292730785.jpg",
    sections: [
      {
        h2: "The honest answer: 2 to 6 weeks",
        body:
          "A typical Liverpool corporate video project takes **2 to 6 weeks** from signed brief to final delivery. Most of our work lands at the 3-week mark. Here's where that time goes:\n\n• Week 1: Pre-production (briefing, scripting, scheduling, location recces)\n• Week 2: Filming day(s) and rough cut\n• Week 3: Editing, motion graphics, music, revisions and delivery\n\nLonger projects (multi-day shoots, animation, multi-location campaigns) stretch into weeks 4, 5 and 6.",
      },
      {
        h2: "Week 1: Pre-production (5 to 7 days)",
        body:
          "This is where most of the value is created. Skip it and you'll spend it in re-edits later.\n\n• **Days 1, 2:** Kick-off call to confirm goals, audience, deliverables, deadlines.\n• **Days 3, 4:** Script or shot list, depending on whether it's interview-led or scripted.\n• **Days 4, 5:** Schedule with your team, recce the location, confirm crew, brief any interviewees.\n• **Days 6, 7:** Final sign-off on script, shot list and call sheet. You see exactly what's going to be filmed before we turn up.",
      },
      {
        h2: "Week 2: Filming (1 to 3 days)",
        body:
          "For a single-location corporate piece, one filming day is usually enough. Two days if there are multiple locations, lots of interviewees, or drone footage as well.\n\n**On the day:** crew arrive 60-90 minutes before the first shot to set up lighting and sound. Filming runs 7-9 hours. We back up the footage to two drives before anyone goes home.",
      },
      {
        h2: "Week 3: Edit, revisions, delivery (5 to 10 days)",
        body:
          "• **Days 1-3:** First cut. Music, graphics, colour grade, captions.\n• **Day 4:** You watch the first cut and send feedback.\n• **Days 5-7:** Revisions round 1.\n• **Day 8:** You watch round 2 and send any final notes.\n• **Days 9-10:** Final revisions. Files exported in every format you need (16:9, 9:16, 1:1 for social, broadcast spec for TV if needed).\n\nDelivery is usually a clean Dropbox folder with the master film, all cut-downs, raw footage and the stills photography we shot at the same time.",
      },
      {
        h2: "What pushes the timeline beyond 3 weeks",
        body:
          "• **Animation or motion graphics-heavy projects:** add 1-2 weeks. Animation simply takes longer.\n• **Multiple deliverables:** five edits take longer than one. Plan a week per major edit.\n• **Multi-day shoots in different locations:** logistics + travel time adds days.\n• **Slow feedback loops:** if your CEO is away for a week, the timeline pauses for a week. We can't control that, but we'll flag it on day one.",
      },
      {
        h2: "The fastest we've turned a video around",
        body:
          "Our record is **48 hours**, brief Monday morning, final delivery Wednesday morning. It was a 60-second highlight reel for an event that finished Sunday night. Two camera operators, one editor working overnight, and a client who was decisive on revisions.\n\nWe don't recommend that pace. But it shows what's possible when the brief is tight, the crew is local, and everyone says yes quickly.",
      },
      {
        h2: "How to keep your project on schedule",
        body:
          "• Nominate **one decision-maker** for sign-off (not a 5-person committee).\n• Confirm everyone you want on camera at briefing stage, not the day before.\n• Reply to first-cut feedback within 24 hours.\n• Don't add new requirements mid-project (a one-week extension on day 14 is much cheaper than rebuilding on day 21).",
      },
    ],
    cta: {
      text: "Need video done in 3 weeks? Send us your brief, we'll send you a realistic schedule within 4 hours.",
      href: "/contact",
    },
  },
  {
    slug: "drone-filming-liverpool-guide-2026",
    title: "Drone Filming in Liverpool: The 2026 Complete Guide (Property, Events, Brand Films)",
    metaTitle: "Drone Filming Liverpool 2026 Guide | Property, Events, Brand Films",
    metaDescription:
      "Everything you need to know about hiring a drone operator in Liverpool in 2026, CAA rules, what 4K aerial filming costs, and the shots that work.",
    keywords:
      "drone filming liverpool, drone operator liverpool, aerial filming liverpool, drone photography liverpool, caa drone pilot liverpool, drone hire liverpool, drone footage liverpool, property drone liverpool",
    publishedAt: "2026-01-09",
    readTime: "8 min read",
    excerpt:
      "Hiring a drone operator in Liverpool isn't quite as simple as Googling 'drone Liverpool' and picking the cheapest. CAA rules, insurance, airspace restrictions, the lot, broken down.",
    cover:
      "/thumbs/665944961.jpg",
    sections: [
      {
        h2: "What you can fly in Liverpool",
        body:
          "Liverpool is a mixed-airspace city. Bits of it are flyable without permissions. Other bits (anywhere near John Lennon Airport, the docks, the city centre, the new build-zones in Kirkby) need a notification or a permission first.\n\nA CAA-certified operator will know which is which without having to call you back twice. If your drone operator can't tell you the airspace classification of your shoot location on the first call, that's data.",
      },
      {
        h2: "The 3 documents your drone operator must hold",
        body:
          "• **CAA Operational Authorisation (formerly PfCO)**, proves they're allowed to fly commercially.\n• **Public liability insurance**, minimum £1 million, ideally £5 million for any commercial work near people or property.\n• **A2 CofC or GVC certificate**, pilot competency.\n\nAsk to see all three before any quote becomes binding. Yellow's drone pilot Keith holds all three and we can supply the certificates within an hour of being asked.",
      },
      {
        h2: "What aerial filming in Liverpool typically costs",
        body:
          "Drone filming in Liverpool sits in three broad price brackets:\n\n• **£250-£500** for a half-day, single location, basic 4K footage with a quick rough edit. Fine for an estate-agent listing or a one-off event.\n• **£800-£1,500** for a full day with multiple locations, planned shot list, colour-graded delivery and a brand-ready final edit.\n• **£2,500+** for multi-day shoots, restricted airspace permissions, presenters or actors on the ground, or specialist kit like cinema-grade gimbal drones.\n\nMost Liverpool brand projects sit comfortably in the middle bracket.",
      },
      {
        h2: "The 5 drone shots that earn their keep",
        body:
          "Most aerial footage in marketing videos is forgettable filler. The five that consistently work:\n\n• **The reveal pull-back.** Starts close on a subject, pulls back to show context. Brilliant for property and event films.\n• **The slow orbit.** Drone arcs around a building or product. Works because it shows three dimensions in one shot.\n• **The push-in.** Drone slowly approaches a subject. Used to open films because it builds expectation.\n• **The flythrough.** Drone moves through a space (door, archway, tunnel). Modern and cinematic, but needs FPV skill.\n• **The top-down.** Vertical god-eye shot. Useful for site layouts, construction progress, large events.",
      },
      {
        h2: "What can't be filmed by drone in Liverpool",
        body:
          "Without specific permissions:\n\n• Within 5km of Liverpool John Lennon Airport (most of South Liverpool).\n• Directly over the Pier Head, Albert Dock or any large public gathering.\n• Inside the Mersey Tunnel approach restricted zone.\n• Most parts of Sefton Park or Calderstones Park when events are running.\n\nWith permissions (we handle the application), most of these can be unlocked, but it adds 5-14 days to the project timeline.",
      },
      {
        h2: "Drone for property: the Liverpool sweet spot",
        body:
          "Drone footage doubles the perceived value of a listing or development film. Yellow has filmed for DeTrafford and other Liverpool developers, with quarterly aerial updates that turn a construction site into investor confidence.\n\nThe trick isn't the drone itself, it's the combination of drone + ground-level cinematography. Drone alone looks like an estate agent's listing. Drone + ground crew looks like a brand film.",
      },
      {
        h2: "Drone for events: do it once, do it right",
        body:
          "Festivals, weddings, conferences, sporting events. The two rules:\n\n• Confirm airspace permissions **2 weeks** before the event.\n• Brief the venue. Some Liverpool venues (Tobacco Warehouse, the Anglican Cathedral grounds, Liverpool ONE) have their own drone policies on top of CAA rules.\n\nWe handle both as part of the production package.",
      },
      {
        h2: "When NOT to use a drone",
        body:
          "Drone footage feels expensive (which is why people love it), but sometimes it just isn't right:\n\n• Indoor product films (use a slider or gimbal instead).\n• Tight emotional storytelling (drone takes you out of the moment).\n• Anywhere a 90-second walk-and-talk on a phone would do the job.\n\nA good Liverpool drone operator will tell you when NOT to use them. That's usually a sign they're worth hiring when you do need them.",
      },
    ],
    cta: {
      text: "Need drone footage in Liverpool? CAA-licensed quote within 4 hours.",
      href: "/drone-filming-aerial-photography-liverpool",
    },
  },
];

export const getPost = (slug) => BLOG_POSTS.find((p) => p.slug === slug);
